import { css } from '@emotion/core'
import { useFeatureFlags } from '@fs/zion-flags'
import {
  createFieldValue,
  generateSls,
  getImage,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  INDEXED_INCORRECTLY,
  saveSlsAndUpdateState,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { Button, colors, QuickGlanceOverlay, Row, TextField, useOverlay } from '@fs/zion-ui'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { stripGarbageCharacters } from '../../../../../shared/utils/stringNormalization'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2Highlights, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { FullTextApp, headers } from '../../../../../shared/services/config'
import { setDisplayHighlights } from '../../../../utils/imageHighlightUtils'
import {
  IMAGE_HOVER_WORD_CLASS,
  hideHoverRects,
  registerTranscript,
  setHitTestRects,
  setWordRects,
  showHoverRects,
} from '../../../../utils/hoverOverlayController'
import { useFullTextStateContext } from '../../providers/FullTextContext'

const keywordCss = css`
  font-weight: bold;
  padding: 0 4px;
  border-radius: 4px;
  background: none;
  border: none;
`

const buttonCss = css`
  background: none;
  border: none;
  padding: 0 2px;
`

const editableCss = css`
  border: 1px solid ${colors.gray10};
  border-radius: 4px;
  padding: 5px;
`

const redactedCss = css`
  color: ${colors.red50};
`

// A word is called out the same way whether the pointer is on it or on its rect in the
// image. The image side only toggles a class, so this costs no render. text-shadow fakes
// bold because real bold glyphs are wider and would rewrap the line under the pointer.
// !important beats the inline background keyword words carry.
const hoverWordsCss = css`
  [data-field-id]:hover,
  .${IMAGE_HOVER_WORD_CLASS} {
    border-radius: 4px;
    background-color: ${colors.blue05} !important;
    text-shadow:
      0.02em 0 0 currentColor,
      -0.02em 0 0 currentColor;
  }

  /* Inside a selection the native selection colour paints over the background above, so a
     called-out word repeats it as its selection colour and looks the same as anywhere else. */
  [data-field-id]:hover::selection,
  .${IMAGE_HOVER_WORD_CLASS}::selection {
    background-color: ${colors.blue05};
    color: ${colors.gray100};
  }
`

const EMPTY_HIGHLIGHTS = []

const forImage = (highlights, imageArk) =>
  highlights?.filter((hl) => !hl.imageId || hl.imageId === imageArk) || EMPTY_HIGHLIGHTS

// The highlights of keyword words on one image, marked attached for the viewer's store.
const keywordHighlightsFor = (imageArk, highlights, keywordFields) =>
  highlights
    ?.filter(
      (highlight) =>
        highlight.imageId === imageArk &&
        highlight.fieldId &&
        keywordFields?.some((field) => field.id === highlight.fieldId)
    )
    ?.map((highlight) => ({ ...highlight, attached: true })) || []

const groupByFieldId = (highlights) => {
  const byFieldId = new Map()
  highlights.forEach((hl) => {
    if (!byFieldId.has(hl.fieldId)) byFieldId.set(hl.fieldId, [])
    byFieldId.get(hl.fieldId).push(hl)
  })
  return byFieldId
}

const Transcript = ({
  fields,
  canViewRedactedContent,
  keywordFields,
  showKeywords,
  allHighlights,
  wordHighlights: wordBoxes,
}) => {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const dispatch = useSlsDispatchContext()
  const r2State = useR2StateContext()
  const fullTextState = useFullTextStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const [editingWord, setEditingWord] = useState(null)
  const [editingWordValue, setEditingWordValue] = useState('')
  const [originalValue, setOriginalValue] = useState('')
  const editQuickGlanceOverlay = useOverlay()
  const transcriptRef = useRef(null)
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
    rip_sharedImageViewer_fullTextHoverAllWords: hoverAllWordsFlag,
  } = useFeatureFlags([
    'rip_sharedImageViewer_saveChanges',
    'rip_sharedImageViewer_sls_actions_splunk_debugger',
    'rip_sharedImageViewer_fullTextHoverAllWords',
  ])
  const hoverAllWords = Boolean(hoverAllWordsFlag?.isOn)

  const currentImage = useMemo(() => getImage(state?.images, state?.currentImageArk) || {}, [state])

  const { displayHighlightsApi, splitScreenDisplayHighlightsApi } = useR2Highlights()

  const setHighlights = useCallback(() => {
    const filterEditHighlights = (imageArk, highlights) => keywordHighlightsFor(imageArk, highlights, keywordFields)
    if (showKeywords) {
      const imageHighlights = filterEditHighlights(state.currentImageArk, allHighlights)
      setDisplayHighlights(imageHighlights, displayHighlightsApi)
      if (r2State.splitScreen) {
        setDisplayHighlights(
          filterEditHighlights(thumbnails?.[r2State.referenceFocusedIndex]?.ark, r2State.referenceHighlights),
          splitScreenDisplayHighlightsApi
        )
      }
    } else {
      setDisplayHighlights(null, displayHighlightsApi)
      setDisplayHighlights(null, splitScreenDisplayHighlightsApi)
    }
  }, [
    allHighlights,
    displayHighlightsApi,
    r2State.referenceFocusedIndex,
    r2State.referenceHighlights,
    r2State.splitScreen,
    splitScreenDisplayHighlightsApi,
    state.currentImageArk,
    thumbnails,
    showKeywords,
    keywordFields,
  ])

  const transcript = useMemo(() => {
    const displayValue = (field) => stripGarbageCharacters(getMostRecentFieldValue(field))

    const getFieldKey = (field, index) => {
      let key = field.id
      if (field.superElements?.[0]) {
        key += `_${field.superElements[0].id}`
      }
      key += `_${index}`
      return key
    }

    const clickHandler = (event, word) => {
      editQuickGlanceOverlay.handleClick(event)
      setEditingWord(word)
      const currentValue = getMostRecentFieldValue(word)
      setEditingWordValue(currentValue)
      setOriginalValue(currentValue)
    }

    const renderRedactedField = (field, key) => {
      const redactedText = canViewRedactedContent ? displayValue(field) : null
      if (canViewRedactedContent && !redactedText) return null
      const redactedLabel = t('full-text.redacted', 'Content redacted')
      return (
        <span
          key={key}
          css={redactedText ? redactedCss : undefined}
          title={redactedText ? undefined : redactedLabel}
          aria-label={redactedText ? undefined : redactedLabel}
        >
          {redactedText ? `${redactedText} ` : ' "■■■■■" '}
        </span>
      )
    }

    const handleFieldMouseOver = (fieldId) => {
      const highlights = allHighlights.filter((hl) => hl.fieldId === fieldId).map((hl) => hl.id)
      displayHighlightsApi.hover(highlights)
      if (r2State.splitScreen) {
        const splitScreenHighlights = r2State.referenceHighlights.filter((hl) => hl.fieldId === fieldId)
        splitScreenDisplayHighlightsApi.hover(splitScreenHighlights)
      }
    }

    const handleFieldMouseOut = () => {
      displayHighlightsApi.hover(null)
      if (r2State.splitScreen) {
        splitScreenDisplayHighlightsApi.hover(null)
      }
    }

    // Lets the hover handlers and the image side find a word without per-word handlers.
    const wordProps = (field) => (hoverAllWords ? { 'data-field-id': field.id } : {})

    const renderKeywordField = (field, key) => {
      const highlightColor = field.id === r2State.activeFieldId ? colors.blue05 : colors.yellow05
      const onMouseOver = () => handleFieldMouseOver(field.id)
      const onMouseOut = handleFieldMouseOut
      if (fullTextState.editTranscriptMode) {
        return (
          <span key={key}>
            {showKeywords && (
              <button
                css={keywordCss}
                type="button"
                style={{ backgroundColor: highlightColor }}
                onMouseOver={onMouseOver}
                onFocus={onMouseOver}
                onMouseOut={onMouseOut}
                onBlur={onMouseOut}
                onClick={(event) => clickHandler(event, field)}
                {...wordProps(field)}
              >
                {`${displayValue(field)} `}
              </button>
            )}
            {!showKeywords && (
              <button
                type="button"
                css={buttonCss}
                onClick={(event) => clickHandler(event, field)}
                {...wordProps(field)}
              >{`${displayValue(field)} `}</button>
            )}
          </span>
        )
      }
      return (
        <span key={key}>
          {showKeywords && (
            <span
              css={keywordCss}
              style={{ backgroundColor: highlightColor }}
              onMouseOver={onMouseOver}
              onFocus={onMouseOver}
              onMouseOut={onMouseOut}
              onBlur={onMouseOut}
              {...wordProps(field)}
            >
              {`${displayValue(field)} `}
            </span>
          )}
          {!showKeywords && <span {...wordProps(field)}>{`${displayValue(field)} `}</span>}
        </span>
      )
    }

    return fields?.map((field, index) => {
      const key = getFieldKey(field, index)
      if (field.redacted) return renderRedactedField(field, key)
      if (field.isKeyword) return renderKeywordField(field, key)
      return fullTextState.editTranscriptMode ? (
        <button
          key={key}
          type="button"
          css={buttonCss}
          onClick={(event) => clickHandler(event, field)}
          {...wordProps(field)}
        >{`${displayValue(field)} `}</button>
      ) : (
        <span key={key} {...wordProps(field)}>{`${displayValue(field)} `}</span>
      )
    })
    // editQuickGlanceOverlay is changing on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    hoverAllWords,
    displayHighlightsApi,
    splitScreenDisplayHighlightsApi,
    allHighlights,
    r2State.activeFieldId,
    r2State.referenceHighlights,
    r2State.splitScreen,
    showKeywords,
    fullTextState.editTranscriptMode,
  ])

  const editTranscriptField = async () => {
    const id = editingWord.id.replace(/_\d+$/, '')
    const field = getNestedOrFloatingElementById([currentImage], id)
    const splitIdString = editingWord.id.split('_')
    const wordIndex = splitIdString.length > 1 ? splitIdString[splitIdString.length - 1] : 0
    const oldFieldValue = getMostRecentFieldValue(field)
    const splitFieldValue = oldFieldValue.split(' ')
    splitFieldValue[wordIndex] = editingWordValue
    const newFieldValue = splitFieldValue.join(' ')
    let dispatches = []

    if (newFieldValue !== oldFieldValue) {
      dispatches = [
        {
          type: 'ADD_FV',
          value: {
            partitionKey: currentImage.partitionKey,
            fieldValue: createFieldValue(newFieldValue, {
              comment: '',
              reasonId: INDEXED_INCORRECTLY,
              app: FullTextApp,
            }),
            id,
          },
        },
      ]
    }

    const sls = generateSls(dispatches)
    await saveSlsAndUpdateState({
      sls,
      dispatches,
      saveChangesFlag,
      slsActionsSplunkDebugger,
      fsUserAgentChain: headers['FS-User-Agent-Chain'],
      userId: r2State.userId,
      appName: FullTextApp,
      dispatch,
      successHandler: () => {
        setEditingWord(null)
        editQuickGlanceOverlay.close()
      },
      saveInitiatorFilename: 'Transcript.js',
    })
  }

  useEffect(() => {
    setHighlights()
  }, [setHighlights])

  // Rects the hover overlay draws and hit tests, for the main image only. The split
  // screen image's highlights are whole SLS elements, while transcript words are split
  // per word (`${field.id}_${idx}`), so they could never match a word. Keyword rects
  // already live in the viewer's highlight store while keywords are shown, and the
  // viewer hovers those itself, so they are not drawn twice. They are still hit tested,
  // so hovering one on the image calls out and scrolls to its transcript word.
  // Every word on this image, keywords included: a transcript selection draws all of them.
  // These are each word's own boxes, which can differ from the keyword highlights' boxes.
  const wordHighlights = useMemo(
    () =>
      hoverAllWords
        ? forImage(wordBoxes ?? allHighlights, state.currentImageArk).filter((hl) => hl.fieldId)
        : EMPTY_HIGHLIGHTS,
    [hoverAllWords, wordBoxes, allHighlights, state.currentImageArk]
  )
  // Only keywords that the viewer actually draws a box for. A keyword with no keyword box,
  // such as the second word of a name, is drawn by the overlay like any other word.
  const keywordsInStore = useMemo(() => {
    if (!showKeywords || !keywordFields?.length) return new Set()
    const drawn = new Set(forImage(allHighlights, state.currentImageArk).map((hl) => hl.fieldId))
    return new Set(keywordFields.map((field) => field.id).filter((id) => drawn.has(id)))
  }, [showKeywords, keywordFields, allHighlights, state.currentImageArk])
  const overlayHighlights = useMemo(
    () => wordHighlights.filter((hl) => !keywordsInStore.has(hl.fieldId)),
    [wordHighlights, keywordsInStore]
  )
  const highlightsByFieldId = useMemo(() => groupByFieldId(overlayHighlights), [overlayHighlights])

  useEffect(() => {
    setHitTestRects('main', wordHighlights, keywordsInStore)
  }, [wordHighlights, keywordsInStore])

  useEffect(() => {
    setWordRects('main', wordHighlights)
  }, [wordHighlights])

  useEffect(() => {
    if (!hoverAllWords) return undefined
    registerTranscript(transcriptRef.current)
    return () => registerTranscript(null)
  }, [hoverAllWords])

  // One delegated handler pair for the whole transcript instead of a closure per word.
  const showWordOnImage = useCallback(
    (event) => {
      const fieldId = event.target.closest?.('[data-field-id]')?.dataset.fieldId
      if (!fieldId) return
      showHoverRects('main', highlightsByFieldId.get(fieldId))
    },
    [highlightsByFieldId]
  )
  const hideWordOnImage = useCallback(() => hideHoverRects('main'), [])
  const hoverHandlers = hoverAllWords
    ? { onMouseOver: showWordOnImage, onFocus: showWordOnImage, onMouseOut: hideWordOnImage, onBlur: hideWordOnImage }
    : {}

  return (
    <>
      <div
        data-testid="full-text-transcript"
        ref={transcriptRef}
        css={hoverAllWords ? hoverWordsCss : undefined}
        {...hoverHandlers}
      >
        {fullTextState.editTranscriptMode ? <div css={editableCss}>{transcript}</div> : transcript}
      </div>
      <QuickGlanceOverlay {...editQuickGlanceOverlay}>
        <Row>
          <TextField
            data-testid="edit-transcript-input"
            value={editingWordValue}
            onChange={(event) => setEditingWordValue(event.target.value)}
          />
          <Button
            data-test-id="full-text-transcript-submit-edit"
            linkName="transcript-edit-submit"
            disabled={editingWordValue === originalValue}
            onClick={editTranscriptField}
            emphasis="high"
          >
            {t('full-text.submit.button', 'Submit')}
          </Button>
        </Row>
      </QuickGlanceOverlay>
    </>
  )
}

export default Transcript
